import { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Badge } from 'react-bootstrap';
import type {
  Manual,
  ManualSequence,
  CreateManualRequest,
  UpdateManualRequest,
} from '../../../infrastructure/ManualClient';

interface ManualFormProps {
  initialData?: Manual;
  onSubmit: (data: CreateManualRequest | UpdateManualRequest) => void;
  isSubmitting: boolean;
}

export const ManualForm = ({ initialData, onSubmit, isSubmitting }: ManualFormProps) => {
  const [title, setTitle] = useState('');
  const [overview, setOverview] = useState('');
  const [comment, setComment] = useState<string>('');
  const [sequences, setSequences] = useState<ManualSequence[]>([{ sequenceIndex: 0, instruction: '', example: '' }]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setOverview(initialData.overview);
      setComment(initialData.comment || '');
      setSequences([...initialData.sequences].sort((a, b) => a.sequenceIndex - b.sequenceIndex));
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors['title'] = 'Title is required';
    }
    if (!overview.trim()) {
      newErrors['overview'] = 'Overview is required';
    }

    sequences.forEach((seq, index) => {
      if (!seq.instruction.trim()) {
        newErrors[`instruction-${index}`] = 'Instruction is required';
      }
      if (!seq.example.trim()) {
        newErrors[`example-${index}`] = 'Example is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSequence = () => {
    setSequences([
      ...sequences,
      {
        sequenceIndex: sequences.length,
        instruction: '',
        example: '',
      },
    ]);
  };

  const handleRemoveSequence = (index: number) => {
    if (sequences.length <= 1) {
      return;
    }

    const newSequences = sequences.filter((_, i) => i !== index);

    // Update sequenceIndex for all sequences after deletion
    const updatedSequences = newSequences.map((seq, i) => ({
      ...seq,
      sequenceIndex: i,
    }));

    setSequences(updatedSequences);
  };

  const handleSequenceChange = (index: number, field: keyof ManualSequence, value: string | number) => {
    const updatedSequences = [...sequences];
    updatedSequences[index] = {
      ...updatedSequences[index],
      [field]: value,
    } as ManualSequence;
    setSequences(updatedSequences);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit({
        title,
        overview,
        comment: comment.trim() ? comment : null,
        sequences,
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Title*</Form.Label>
        <Form.Control
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          isInvalid={Boolean(errors['title'])}
        />
        <Form.Control.Feedback type="invalid">{errors['title']}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Overview*</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={overview}
          onChange={(e) => setOverview(e.target.value)}
          isInvalid={Boolean(errors['overview'])}
        />
        <Form.Control.Feedback type="invalid">{errors['overview']}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Comment (Optional)</Form.Label>
        <Form.Control as="textarea" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
      </Form.Group>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            Sequences <Badge bg="info">{sequences.length} steps</Badge>
          </h5>
          <Button variant="outline-primary" size="sm" onClick={handleAddSequence}>
            Add Step
          </Button>
        </div>

        {sequences.map((sequence, index) => (
          <Card key={index} className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Badge bg="primary" className="me-2">
                  Step {index + 1}
                </Badge>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRemoveSequence(index)}
                disabled={sequences.length <= 1}
              >
                Remove
              </Button>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Instruction*</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={sequence.instruction}
                      onChange={(e) => handleSequenceChange(index, 'instruction', e.target.value)}
                      isInvalid={Boolean(errors[`instruction-${index}`])}
                    />
                    <Form.Control.Feedback type="invalid">{errors[`instruction-${index}`]}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Example*</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={sequence.example}
                      onChange={(e) => handleSequenceChange(index, 'example', e.target.value)}
                      isInvalid={Boolean(errors[`example-${index}`])}
                    />
                    <Form.Control.Feedback type="invalid">{errors[`example-${index}`]}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}
      </div>

      <div className="d-flex justify-content-end">
        <Button variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Manual'}
        </Button>
      </div>
    </Form>
  );
};
